import type { ReactNode } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { Layout } from './Layout';
import { cn } from '../../lib/cn';

type Tom = 'sucesso' | 'info' | 'aviso';

const TONS: Record<Tom, string> = {
  sucesso: 'bg-emerald-100 text-emerald-600',
  info: 'bg-blue-100 text-blue-600',
  aviso: 'bg-amber-100 text-amber-600',
};

interface PainelResultadoProps {
  /** Título grande (ex.: "Manifestação registrada"). */
  titulo: string;
  /** Texto de apoio abaixo do título. */
  descricao?: ReactNode;
  /** Ícone do círculo no topo. Padrão: check. */
  icone?: ReactNode;
  /** Cor do círculo do ícone. Padrão "sucesso". */
  tom?: Tom;
  /** Conteúdo central (ex.: protocolo, link de acompanhamento). */
  children?: ReactNode;
  /** Botões de ação no rodapé. */
  acoes?: ReactNode;
  /** Não envolver em <Layout>. */
  semLayout?: boolean;
}

/**
 * Tela de resultado padrão (sucesso/confirmação) usada após envio de formulários
 * do portal (Ouvidoria, e-SIC, Zeladoria, Solicitar...). Centralizada e elegante.
 */
export function PainelResultado({
  titulo, descricao, icone, tom = 'sucesso', children, acoes, semLayout,
}: PainelResultadoProps) {
  const corpo = (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-6 py-10 text-center sm:px-10">
          <div className={cn('mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full', TONS[tom])}>
            {icone ?? <FiCheckCircle size={32} />}
          </div>
          <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">{titulo}</h1>
          {descricao && <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">{descricao}</p>}
          {children && <div className="mt-6 text-left">{children}</div>}
          {acoes && <div className="mt-7 flex flex-wrap items-center justify-center gap-2">{acoes}</div>}
        </div>
      </div>
    </div>
  );

  if (semLayout) return <div className="min-h-screen bg-slate-50">{corpo}</div>;
  return <Layout>{corpo}</Layout>;
}
